import { defineStore } from 'pinia'
import portfolioService from '@/api/services/portfolio'

export const usePortfolioStore = defineStore('portfolio', {
  state: () => ({
    // List of portfolios user has access to
    portfolios: [],
    // Active portfolio (user's preference)
    activePortfolioId: null,
    activePortfolioName: null,
    // Temporary portfolio override (X-Portfolio-Id header)
    overridePortfolioId: null,
    // Loading states
    isLoading: false,
    isChangingActive: false,
    // Error state
    error: null,
    // Last loaded timestamp
    lastLoaded: null,
    // User admin status (affects portfolio visibility)
    isAdmin: false
  }),

  getters: {
    /**
     * Get the effective portfolio ID to use for API calls
     * Returns override if set, otherwise active portfolio
     */
    effectivePortfolioId: (state) => {
      return state.overridePortfolioId || state.activePortfolioId
    },

    /**
     * Get the active portfolio object
     */
    activePortfolio: (state) => {
      if (!state.activePortfolioId) return null
      return state.portfolios.find(p => p.id === state.activePortfolioId) || null
    },

    /**
     * Get portfolios the user owns
     */
    ownedPortfolios: (state) => {
      return state.portfolios.filter(p => p.role === 'owner')
    },

    /**
     * Get portfolios where user is a member
     */
    memberPortfolios: (state) => {
      return state.portfolios.filter(p => p.role === 'member')
    },

    /**
     * Check if user can manage a specific portfolio
     */
    canManagePortfolio: (state) => (portfolioId) => {
      if (state.isAdmin) return true
      const portfolio = state.portfolios.find(p => p.id === portfolioId)
      return portfolio?.role === 'owner'
    },

    /**
     * Check if user has any portfolios
     */
    hasPortfolios: (state) => {
      return state.portfolios.length > 0
    },

    /**
     * Get the default portfolio
     */
    defaultPortfolio: (state) => {
      return state.portfolios.find(p => p.isDefault) || null
    },

    /**
     * Check if data needs refresh (older than 5 minutes)
     */
    needsRefresh: (state) => {
      if (!state.lastLoaded) return true
      const fiveMinutesAgo = Date.now() - (5 * 60 * 1000)
      return state.lastLoaded < fiveMinutesAgo
    }
  },

  actions: {
    /**
     * Initialize portfolio state - loads portfolios and active portfolio
     * @param {boolean} force - Force reload even if recently loaded
     */
    async initialize(force = false) {
      // Skip if recently loaded and not forcing
      if (!force && !this.needsRefresh && this.hasPortfolios) {
        return { success: true, fromCache: true }
      }

      this.isLoading = true
      this.error = null

      try {
        // Load portfolios and active portfolio in parallel
        const [portfoliosResult, activeResult] = await Promise.all([
          portfolioService.listPortfolios({ allPortfolios: this.isAdmin }),
          portfolioService.getActivePortfolio()
        ])

        if (portfoliosResult.success) {
          this.portfolios = portfoliosResult.portfolios
        }

        if (activeResult.success) {
          this.activePortfolioId = activeResult.portfolioId
          this.activePortfolioName = activeResult.portfolioName
        }

        this.lastLoaded = Date.now()

        return {
          success: true,
          portfolios: this.portfolios,
          activePortfolioId: this.activePortfolioId
        }
      } catch (error) {
        console.error('Error initializing portfolio store:', error)
        this.error = error.message || 'Failed to load portfolios'
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Set admin status (affects portfolio visibility)
     * @param {boolean} isAdmin - Whether user is admin
     */
    setAdminStatus(isAdmin) {
      this.isAdmin = isAdmin
    },

    /**
     * Load portfolios from backend
     * @param {Object} options - { allPortfolios: boolean }
     */
    async loadPortfolios(options = {}) {
      this.isLoading = true
      this.error = null

      try {
        const result = await portfolioService.listPortfolios({
          allPortfolios: options.allPortfolios ?? this.isAdmin
        })

        if (result.success) {
          this.portfolios = result.portfolios
          this.lastLoaded = Date.now()
        }

        return result
      } catch (error) {
        console.error('Error loading portfolios:', error)
        this.error = error.message || 'Failed to load portfolios'
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Set active portfolio (persists to backend)
     * @param {number} portfolioId - Portfolio ID to activate
     */
    async setActivePortfolio(portfolioId) {
      this.isChangingActive = true
      this.error = null

      try {
        const result = await portfolioService.setActivePortfolio(portfolioId)

        if (result.success) {
          this.activePortfolioId = result.portfolioId
          this.activePortfolioName = result.portfolioName
          // Clear any temporary override when changing active portfolio
          this.overridePortfolioId = null
        }

        return result
      } catch (error) {
        console.error('Error setting active portfolio:', error)
        this.error = error.message || 'Failed to change portfolio'
        return { success: false, error: this.error }
      } finally {
        this.isChangingActive = false
      }
    },

    /**
     * Set temporary portfolio override (for X-Portfolio-Id header)
     * Does not persist to backend
     * @param {number|null} portfolioId - Portfolio ID to override, or null to clear
     */
    setOverridePortfolio(portfolioId) {
      this.overridePortfolioId = portfolioId
    },

    /**
     * Clear temporary portfolio override
     */
    clearOverride() {
      this.overridePortfolioId = null
    },

    /**
     * Create a new portfolio
     * @param {Object} data - { name, description }
     */
    async createPortfolio(data) {
      try {
        const result = await portfolioService.createPortfolio(data)

        if (result.success) {
          // Add to local list
          this.portfolios.push(result.portfolio)
        }

        return result
      } catch (error) {
        console.error('Error creating portfolio:', error)
        throw error
      }
    },

    /**
     * Update a portfolio
     * @param {number} portfolioId - Portfolio ID
     * @param {Object} data - { name, description }
     */
    async updatePortfolio(portfolioId, data) {
      try {
        const result = await portfolioService.updatePortfolio(portfolioId, data)

        if (result.success) {
          // Update in local list
          const index = this.portfolios.findIndex(p => p.id === portfolioId)
          if (index !== -1) {
            this.portfolios[index] = result.portfolio
          }
          // Update active name if this is the active portfolio
          if (this.activePortfolioId === portfolioId && data.name) {
            this.activePortfolioName = data.name
          }
        }

        return result
      } catch (error) {
        console.error('Error updating portfolio:', error)
        throw error
      }
    },

    /**
     * Delete a portfolio
     * @param {number} portfolioId - Portfolio ID
     */
    async deletePortfolio(portfolioId) {
      try {
        const result = await portfolioService.deletePortfolio(portfolioId)

        if (result.success) {
          // Remove from local list
          this.portfolios = this.portfolios.filter(p => p.id !== portfolioId)
          // Clear active if this was the active portfolio
          if (this.activePortfolioId === portfolioId) {
            // Try to set to default portfolio
            const defaultPortfolio = this.defaultPortfolio
            if (defaultPortfolio) {
              await this.setActivePortfolio(defaultPortfolio.id)
            } else {
              this.activePortfolioId = null
              this.activePortfolioName = null
            }
          }
        }

        return result
      } catch (error) {
        console.error('Error deleting portfolio:', error)
        throw error
      }
    },

    /**
     * Refresh portfolio counts (after adding/removing companies)
     * @param {number} portfolioId - Portfolio ID to refresh
     */
    async refreshPortfolioCounts(portfolioId) {
      try {
        const result = await portfolioService.getPortfolio(portfolioId)
        if (result.success) {
          const index = this.portfolios.findIndex(p => p.id === portfolioId)
          if (index !== -1) {
            this.portfolios[index] = result.portfolio
          }
        }
      } catch (error) {
        console.error('Error refreshing portfolio counts:', error)
      }
    },

    /**
     * Clear all state (for logout)
     */
    clearState() {
      this.portfolios = []
      this.activePortfolioId = null
      this.activePortfolioName = null
      this.overridePortfolioId = null
      this.isLoading = false
      this.isChangingActive = false
      this.error = null
      this.lastLoaded = null
      this.isAdmin = false
    }
  }
})
